import React from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">Archery Manager AI</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500 mb-8">Effective Date: February 2026</p>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 leading-relaxed">
              At Archery Manager AI, we value your privacy and are committed to protecting your information.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Information We Collect</h2>
            <p className="text-slate-700 leading-relaxed mb-2">We may collect:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Name and email address</li>
              <li>User role (parent, archer, coach)</li>
              <li>Event participation data</li>
              <li>Performance data entered by users</li>
              <li>Communication preferences</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4 font-medium">
              We do not sell personal data.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. Information About Minors</h2>
            <p className="text-slate-700 leading-relaxed mb-2">Accounts may include data related to minor athletes.</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Parents or guardians must provide consent</li>
              <li>We collect only the minimum data necessary for functionality</li>
              <li>We do not use youth data for advertising</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. How We Use Information</h2>
            <p className="text-slate-700 leading-relaxed mb-2">We use information to:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Provide platform access</li>
              <li>Send account and event notifications</li>
              <li>Improve product features</li>
              <li>Generate aggregated performance insights</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. Emails & Notifications</h2>
            <p className="text-slate-700 leading-relaxed mb-2">Users may receive:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Account approval emails</li>
              <li>Event reminders</li>
              <li>Platform updates</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-2">
              You may opt out of non-essential communications at any time.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. Data Sharing</h2>
            <p className="text-slate-700 leading-relaxed mb-2">We do not share personal data with third parties except:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Email service providers (for notifications)</li>
              <li>Hosting and infrastructure providers</li>
              <li>When required by law</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Data Security</h2>
            <p className="text-slate-700 leading-relaxed">
              We implement reasonable technical and organizational safeguards to protect user data. However, no system is 100% secure.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Cookies</h2>
            <p className="text-slate-700 leading-relaxed mb-2">We may use cookies or similar technologies for:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Session management</li>
              <li>Analytics</li>
              <li>Performance monitoring</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. Your Rights</h2>
            <p className="text-slate-700 leading-relaxed mb-2">You may:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Request access to your data</li>
              <li>Request corrections or deletion</li>
              <li>Request account removal</li>
            </ul>
            <div className="flex items-center gap-2 mt-2 text-primary">
              <Mail className="w-4 h-4" />
              <a href="mailto:privacy@archerymanagerai.com" className="hover:underline">
                privacy@archerymanagerai.com
              </a>
            </div>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. Changes to This Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this policy as the platform evolves. Updates will be posted on this page.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. Contact</h2>
            <p className="text-slate-700 leading-relaxed">
              For privacy questions:
            </p>
            <div className="flex items-center gap-2 mt-2 text-primary">
              <Mail className="w-4 h-4" />
              <a href="mailto:privacy@archerymanagerai.com" className="hover:underline">
                privacy@archerymanagerai.com
              </a>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          <span className="mx-2">•</span>
          <Link to="/login" className="text-primary hover:underline">Back to Login</Link>
        </div>
      </main>
    </div>
  );
}
